/**
 * Links Admin Page - Edit helpful Links & Resources for Roosevelt Cheer
 *
 * @actor Admin {Person} {in} RHS Boosters, team admins
 */

import { useState, useEffect } from 'react'
import { useLinks, usePublishLinks } from '../hooks/useLinks'
import { linksDataSchema } from '../utils/validation'
import { LinkCard } from '../components/LinkCard'
import { LinkForm } from '../components/LinkForm'
import { Modal } from '../components/ui/Modal'
import { Button } from '../components/ui/Button'
import { Alert } from '../components/ui/Alert'
import { Spinner } from '../components/ui/Spinner'
import { Plus } from 'lucide-react'
import type { Link } from '../types'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'

interface EditingState {
  isOpen: boolean
  index: number | null
  link: Link
}

export function LinksPage() {
  const { data, isLoading, error } = useLinks()
  const publishMutation = usePublishLinks()

  const [draftLinks, setDraftLinks] = useState<Link[]>([])
  const [etag, setEtag] = useState<string>('')
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [editing, setEditing] = useState<EditingState>({
    isOpen: false,
    index: null,
    link: { label: '', href: '' },
  })

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Load initial data
  useEffect(() => {
    if (data) {
      setDraftLinks(data.data.links)
      setEtag(data.etag)
    }
  }, [data])

  // Check if dirty
  const isDirty = JSON.stringify(draftLinks) !== JSON.stringify(data?.data.links || [])

  // Open modal for new link
  const startAddLink = () => {
    setEditing({
      isOpen: true,
      index: null,
      link: { label: '', href: '' },
    })
  }

  // Open modal for editing existing link
  const startEditLink = (index: number) => {
    setEditing({
      isOpen: true,
      index,
      link: { ...draftLinks[index] },
    })
  }

  // Save link from modal
  const saveLink = () => {
    if (editing.index === null) {
      // Add new link
      setDraftLinks([...draftLinks, editing.link])
    } else {
      // Update existing link
      const newLinks = [...draftLinks]
      newLinks[editing.index] = editing.link
      setDraftLinks(newLinks)
    }
    setEditing({ isOpen: false, index: null, link: { label: '', href: '' } })
  }

  // Delete link
  const deleteLink = (index: number) => {
    if (confirm('Are you sure you want to delete this link?')) {
      setDraftLinks(draftLinks.filter((_, i) => i !== index))
    }
  }

  // Move link up
  const moveUp = (index: number) => {
    if (index === 0) return
    const newLinks = [...draftLinks]
    ;[newLinks[index - 1], newLinks[index]] = [newLinks[index], newLinks[index - 1]]
    setDraftLinks(newLinks)
  }

  // Move link down
  const moveDown = (index: number) => {
    if (index === draftLinks.length - 1) return
    const newLinks = [...draftLinks]
    ;[newLinks[index], newLinks[index + 1]] = [newLinks[index + 1], newLinks[index]]
    setDraftLinks(newLinks)
  }

  // Handle drag and drop reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = draftLinks.findIndex((_, i) => i === active.id)
      const newIndex = draftLinks.findIndex((_, i) => i === over.id)
      setDraftLinks(arrayMove(draftLinks, oldIndex, newIndex))
    }
  }

  // Validate before publish
  const handlePublish = async () => {
    const validation = linksDataSchema.safeParse({ links: draftLinks })

    if (!validation.success) {
      setValidationErrors(validation.error.errors.map((e) => e.message))
      return
    }

    try {
      await publishMutation.mutateAsync({ links: draftLinks, etag })
      setValidationErrors([])
    } catch (error) {
      const err = error as Error
      setValidationErrors([err.message])
    }
  }

  if (isLoading) return <Spinner />
  if (error) return <Alert variant="error">{(error as Error).message}</Alert>

  return (
    <>
      {/* Sticky Header - Compact on short viewports */}
      <div className="sticky top-0 z-10 bg-white border-b border-coolgray shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 sm:py-4 max-h-[200px]:py-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-heading text-rhs-maroon truncate">
                Links
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {draftLinks.length} link{draftLinks.length !== 1 ? 's' : ''}
                {isDirty && <span className="text-rhs-gold font-semibold ml-2">• Unsaved</span>}
              </p>
            </div>
            <Button onClick={startAddLink} className="flex items-center gap-2 flex-shrink-0">
              <Plus size={18} className="sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Add</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert variant="error" className="mb-4">
            {validationErrors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </Alert>
        )}

        {/* Success Message */}
        {publishMutation.isSuccess && (
          <Alert variant="success" className="mb-4">
            Links published successfully!
          </Alert>
        )}

        {/* Links List */}
        {draftLinks.length === 0 ? (
          <div className="bg-ice-dark rounded-card p-8 sm:p-12 text-center border border-coolgray">
            <p className="text-gray-600 text-base sm:text-lg font-title mb-4">No links yet</p>
            <Button onClick={startAddLink} className="flex items-center gap-2 mx-auto">
              <Plus size={20} />
              Add Your First Link
            </Button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={draftLinks.map((_, i) => i)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2 mb-20">
                {draftLinks.map((link, index) => (
                  <LinkCard
                    key={index}
                    id={index}
                    link={link}
                    onEdit={() => startEditLink(index)}
                    onDelete={() => deleteLink(index)}
                    onMoveUp={index > 0 ? () => moveUp(index) : undefined}
                    onMoveDown={index < draftLinks.length - 1 ? () => moveDown(index) : undefined}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Fixed Bottom Bar - Hide on very short viewports */}
        {draftLinks.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-rhs-gold shadow-gold-lg p-3 sm:hidden landscape:max-h-[500px]:hidden">
            <div className="flex gap-2">
              <Button
                onClick={handlePublish}
                disabled={!isDirty || publishMutation.isPending}
                className="flex-1 text-sm"
              >
                {publishMutation.isPending ? 'Publishing...' : 'Publish'}
              </Button>
              <Button
                onClick={() => setDraftLinks(data?.data.links || [])}
                disabled={!isDirty}
                variant="secondary"
                className="text-sm"
              >
                Reset
              </Button>
            </div>
          </div>
        )}

        {/* Desktop Publish Bar */}
        {draftLinks.length > 0 && (
          <div className="hidden sm:flex gap-3 sticky bottom-4 bg-white p-4 rounded-card shadow-gold-lg border border-rhs-gold">
            <Button
              onClick={handlePublish}
              disabled={!isDirty || publishMutation.isPending}
              className="flex-1"
            >
              {publishMutation.isPending ? 'Publishing...' : 'Publish Changes'}
            </Button>
            <Button
              onClick={() => setDraftLinks(data?.data.links || [])}
              disabled={!isDirty}
              variant="secondary"
            >
              Reset
            </Button>
          </div>
        )}
      </div>

      {/* Edit/Add Modal */}
      <Modal
        isOpen={editing.isOpen}
        onClose={() => setEditing({ isOpen: false, index: null, link: { label: '', href: '' } })}
        title={editing.index === null ? 'Add Link' : 'Edit Link'}
      >
        <LinkForm
          link={editing.link}
          onChange={(updatedLink) => setEditing({ ...editing, link: updatedLink })}
          onSave={saveLink}
          onCancel={() => setEditing({ isOpen: false, index: null, link: { label: '', href: '' } })}
        />
      </Modal>
    </>
  )
}
